import AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';
import attachmentItemsPagingDetails from 'owa-service/lib/factory/attachmentItemsPagingDetails';
import AttachmentItemsPagingMetadata from 'owa-service/lib/contract/AttachmentItemsPagingMetadata';
import AttachmentItemsSort from 'owa-service/lib/contract/AttachmentItemsSort';
import canCallFileProviderAPIFromClient from '../getFileProviderItems/canCallFileProviderAPIFromClient';
import getAttachmentDataProviderItemsOperation from 'owa-service/lib/operation/getAttachmentDataProviderItemsOperation';
import GetAttachmentDataProviderItemsRequest from 'owa-service/lib/contract/GetAttachmentDataProviderItemsRequest';
import GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import getBoxFileProviderItems from '../getFileProviderItems/getBoxFileProviderItems';
import getDropboxFileProviderItems from '../getFileProviderItems/getDropboxFileProviderItems';
import getFacebookFileProviderItems from '../getFileProviderItems/getFacebookFileProviderItems';
import getGDriveFileProviderItems from './getGDriveFileProviderItems';
import getMailMessageFileProviderItems from '../getFileProviderItems/getMailMessageFileProviderItems';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import oneDriveProScope from 'owa-service/lib/factory/oneDriveProScope';
import OneDriveProScope from 'owa-service/lib/contract/OneDriveProScope';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import { defaultSort } from '../constants';
import { getGDriveQueryString } from '../utils/getGDriveQueryString';
import { getOneDriveProFileProviderItems } from '../getFileProviderItems/getOneDriveFileProviderItems';
import { isOneDriveProItemsSkipTokenPagingMetadata, isOneDriveProScope } from '../utils/typeGuards';
import { OneDriveProItemsSkipTokenPagingMetadata } from '../types/OneDriveProItemsSkipTokenPagingMetadata';
import { trace } from 'owa-trace';

const defaultScope: OneDriveProScope = oneDriveProScope({
    Type: OneDriveProScopeType.MyDocuments,
    DisplayName: 'Files',
    AriaLabel: 'Files',
});

export function callServiceToGetFileProviderItems(
    fileProviderId: string,
    offset: number,
    filterBy: AttachmentItemsFilterBy,
    folderId?: string,
    location?: string,
    scope?: AttachmentDataProviderScope,
    pagingMetadata?: AttachmentItemsPagingMetadata,
    sort?: AttachmentItemsSort
): Promise<GetAttachmentDataProviderItemsResponse> {
    const request: GetAttachmentDataProviderItemsRequest = {
        AttachmentDataProviderId: fileProviderId,
        Paging: attachmentItemsPagingDetails({
            Sort: sort || defaultSort,
            FolderId: folderId || '',
            Location: location || '',
            FilterBy: filterBy,
            RequestedData: indexedPageView({
                BasePoint: 'Beginning',
                Offset: offset,
                MaxEntriesReturned: 100,
            }),
            PagingMetadata: pagingMetadata,
        }),
        Scope: scope == null ? defaultScope : scope,
    };

    return getAttachmentDataProviderItemsOperation({ request: request });
}

export async function getFileProviderItems(
    fileProviderType: AttachmentDataProviderType,
    fileProviderId: string,
    offset: number,
    filterBy: AttachmentItemsFilterBy,
    scope: AttachmentDataProviderScope = null,
    fileProviderEndpointUrl: string = null,
    folderId?: string,
    location?: string,
    pagingMetadata: AttachmentItemsPagingMetadata = null,
    sort?: AttachmentItemsSort
): Promise<GetAttachmentDataProviderItemsResponse> {
    try {
        const canCallAPIFromClient = await canCallFileProviderAPIFromClient(
            fileProviderType,
            fileProviderEndpointUrl
        );

        if (canCallAPIFromClient) {
            if (fileProviderType === AttachmentDataProviderType.GDrive) {
                return await getGDriveFileProviderItems(getGDriveQueryString(location, sort));
            } else if (fileProviderType === AttachmentDataProviderType.Dropbox) {
                const sortColumn = sort ? sort.SortColumn : defaultSort.SortColumn;
                const sortOrder = sort ? sort.SortOrder : defaultSort.SortOrder;
                return await getDropboxFileProviderItems(location, sortColumn, sortOrder);
            } else if (fileProviderType === AttachmentDataProviderType.Box) {
                const sortColumn = sort ? sort.SortColumn : defaultSort.SortColumn;
                const sortOrder = sort ? sort.SortOrder : defaultSort.SortOrder;
                return await getBoxFileProviderItems(folderId, sortColumn, sortOrder);
            } else if (fileProviderType === AttachmentDataProviderType.Facebook) {
                return await getFacebookFileProviderItems(scope);
            } else if (
                fileProviderType === AttachmentDataProviderType.OneDrivePro &&
                (!scope ||
                    (isOneDriveProScope(scope) && scope.Type === OneDriveProScopeType.MyDocuments))
            ) {
                if (pagingMetadata && !isOneDriveProItemsSkipTokenPagingMetadata(pagingMetadata)) {
                    throw new Error(
                        'The paging metadata is not of type OneDriveProItemsSkipTokenPagingMetadata'
                    );
                }

                // We are calling client side call for ODB if the flight is turned on
                // on, the provider is OneDrivePro and the scope is either null (in which
                // case we will use the 'Files' scope by default) or the scope is 'Files'
                return await getOneDriveProFileProviderItems(
                    fileProviderEndpointUrl,
                    fileProviderId,
                    sort ? sort.SortColumn : defaultSort.SortColumn,
                    sort ? sort.SortOrder : defaultSort.SortOrder,
                    folderId,
                    {
                        metadata: pagingMetadata as OneDriveProItemsSkipTokenPagingMetadata,
                        maxEntries: 100,
                    }
                );
            } else if (fileProviderType === AttachmentDataProviderType.MailMessage) {
                return await getMailMessageFileProviderItems(folderId, offset);
            }
        }

        // If we can't make the API call from client or there is no client side
        // API supported for the provider then make the call to server to get items
        return await callServiceToGetFileProviderItems(
            fileProviderId,
            offset,
            filterBy,
            folderId,
            location,
            scope,
            pagingMetadata,
            sort
        );
    } catch (error) {
        trace.warn('FilePicker: getFileProviderItems got error: ' + error.message);
        throw error;
    }
}
